/**
 * Card Media Slider — extends the built-in SliderComponent
 * to add dot indicator updates and prevent link navigation on arrow clicks.
 *
 * This script is loaded on pages with product grids.
 * It hooks into the existing SliderComponent (slider-component custom element)
 * for all instances with [data-card-slider].
 */
(function () {
  'use strict';

  // Each slider is wired up once. The script used to be included per card, so
  // every slider on the page was re-initialised once per card on the page.
  const initialisedSliders = new WeakSet();

  function initCardSliders() {
    const sliders = document.querySelectorAll('card-slider');

    sliders.forEach((slider) => {
      if (initialisedSliders.has(slider)) return;
      initialisedSliders.add(slider);
      const list = slider.querySelector('.card-media-slider__list');
      const dots = slider.querySelectorAll('.card-media-slider__dot');
      const prevBtn = slider.querySelector('[name="previous"]');
      const nextBtn = slider.querySelector('[name="next"]');
      const slides = slider.querySelectorAll('.card-media-slider__slide');

      if (!list || slides.length <= 1) return;

      // Prevent <a> navigation when clicking arrows
      [prevBtn, nextBtn].forEach((btn) => {
        if (!btn) return;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        // Also prevent mousedown from triggering the link
        btn.addEventListener('mousedown', (e) => {
          e.stopPropagation();
        });
      });

      // Update dots and button states on scroll
      let scrollTimeout;
      list.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const scrollLeft = list.scrollLeft;
          const slideWidth = list.offsetWidth;
          const currentIndex = Math.round(scrollLeft / slideWidth);

          // Update dots
          dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === currentIndex);
          });

          // Update button disabled states
          if (prevBtn) {
            prevBtn.disabled = currentIndex === 0;
          }
          if (nextBtn) {
            nextBtn.disabled = currentIndex >= slides.length - 1;
          }
        }, 50);
      });

      // Arrow click handlers (since we prevent default, we handle scrolling manually)
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          const slideWidth = list.offsetWidth;
          list.scrollBy({ left: -slideWidth, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          const slideWidth = list.offsetWidth;
          list.scrollBy({ left: slideWidth, behavior: 'smooth' });
        });
      }

      // --- Mouse drag support for desktop ---
      let isDragging = false;
      let startX = 0;
      let scrollStart = 0;
      let hasDragged = false;

      list.addEventListener('mousedown', (e) => {
        // Ignore if clicking on a button
        if (e.target.closest('.card-media-slider__btn')) return;
        isDragging = true;
        hasDragged = false;
        startX = e.pageX;
        scrollStart = list.scrollLeft;
        list.style.cursor = 'grabbing';
        list.style.scrollSnapType = 'none';
        list.style.scrollBehavior = 'auto';
        e.preventDefault();
      });

      list.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const dx = e.pageX - startX;
        if (Math.abs(dx) > 5) hasDragged = true;
        list.scrollLeft = scrollStart - dx;
      });

      const endDrag = () => {
        if (!isDragging) return;
        isDragging = false;
        list.style.cursor = '';
        list.style.scrollSnapType = '';
        list.style.scrollBehavior = '';

        // Snap to nearest slide
        const slideWidth = list.offsetWidth;
        const dragDist = list.scrollLeft - scrollStart;
        const threshold = 30;

        if (Math.abs(dragDist) > threshold) {
          const direction = dragDist > 0 ? 1 : -1;
          const currentIndex = Math.round(scrollStart / slideWidth);
          const targetIndex = Math.max(0, Math.min(slides.length - 1, currentIndex + direction));
          list.scrollTo({ left: targetIndex * slideWidth, behavior: 'smooth' });
        } else {
          // Snap back
          const currentIndex = Math.round(scrollStart / slideWidth);
          list.scrollTo({ left: currentIndex * slideWidth, behavior: 'smooth' });
        }
      };

      list.addEventListener('mouseup', endDrag);
      list.addEventListener('mouseleave', endDrag);

      // Prevent click on links after drag
      slider.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          hasDragged = false;
        }
      }, true);
    });

  }

  // Mobile: move quick-add into card__price-row for flex alignment
  function alignQuickAddMobile() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const cards = document.querySelectorAll('.card--card');

    cards.forEach((card) => {
      const priceRow = card.querySelector('.card__price-row');
      const quickAdd = card.querySelector('.quick-add');
      if (!priceRow || !quickAdd) return;

      if (isMobile && quickAdd.parentElement !== priceRow) {
        // Store original parent for restoring on desktop
        quickAdd._originalParent = quickAdd.parentElement;
        quickAdd._originalNext = quickAdd.nextElementSibling;
        priceRow.appendChild(quickAdd);
      } else if (!isMobile && quickAdd._originalParent && quickAdd.parentElement === priceRow) {
        // Restore to original position on desktop
        if (quickAdd._originalNext) {
          quickAdd._originalParent.insertBefore(quickAdd, quickAdd._originalNext);
        } else {
          quickAdd._originalParent.appendChild(quickAdd);
        }
      }
    });
  }

  // Let dynamically loaded cards (infinite scroll) be initialised too
  window.initCardSliders = initCardSliders;

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCardSliders();
      alignQuickAddMobile();
    });
  } else {
    initCardSliders();
    alignQuickAddMobile();
  }

  // Re-align on resize (desktop <-> mobile)
  window.addEventListener('resize', alignQuickAddMobile);

  // Also run when new content is loaded (e.g., infinite scroll, AJAX pagination)
  document.addEventListener('shopify:section:load', () => {
    initCardSliders();
    alignQuickAddMobile();
  });
})();
